import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { authFetch } from '../utils/authFetch';
import {
  Container, Stack, Card, Group, Text, Button,
  Badge, Divider, Alert, Transition, Modal, ActionIcon,
  Tooltip, RingProgress, Switch, Progress, Center
} from '@mantine/core';
import {
  IconUpload, IconCheck, IconAlertCircle,
  IconFileText, IconBrain, IconShield, IconTrash,
  IconLoader, IconPlayerPlay, IconPlayerPause, IconFileDescription
} from '@tabler/icons-react';

import RiskNarrativeCard from './components/RiskNarrativeCard';
import RiskAutopilotV2 from './RiskAutopilotV2';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
// Use assessment-based selector instead of legacy RiskFormGenerator
// Legacy depends on state.risks.currentModelId which may not be set during RTK migration
import { createRiskFormSelector } from '../redux/selectors/riskFormGenerator';
// no context field prefill in narrative-first flow
import { uploadViaSupabase } from '../utils/supabaseUpload';
import ModelCardUploadButton from './utils/ModelCardUploadButton';
// RTK async thunks for autopilot operations
import {
  fetchRiskCardData,
  updateAssessmentStatusByUuid,
  initializeAssessmentLocal,
  updateAssessmentPositionLocal,
  setAssessmentCheckpoint,
} from '../redux/slices/assessmentsSlice';
import { fetchAssessmentRisks } from '../redux/slices/risksSlice';
import { fetchResidualRisks } from '../redux/slices/residualRiskSlice';
import { setCurrentAssessment, setCurrentModel } from '../redux/slices/appSlice';
import ResidualRiskCards from '../components/ResidualRiskCards';
// Bridge selectors for gradual migration to assessment-centric state
import {
  selectContextBridge,
  selectSecurityBridge,
  selectSavedControlsBridge
} from '../redux/selectors/assessmentSelectors';
import { saveCheckpoint, clearCheckpoint } from '../redux/slices/assessmentProgressSlice';

/**
 * AutopilotAssessmentFlow - Main orchestrator for AI-driven risk assessment
 * Guides user through: Upload → Context → Risk Generation → Review → Report
 */
export default function AutopilotAssessmentFlow() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { modelId, assessmentUuid } = useParams();
  const { isLoading: authLoading, isAuthenticated } = useAuth0();

  // Redux state via bridge selectors (reads new slice first, falls back to legacy)
  const existingContextData = useSelector(selectContextBridge(assessmentUuid, modelId));
  const modelName = useSelector((s) => s.models?.byId?.[modelId]?.model_name || 'Unknown Model');

  // Get risk taxonomy from assessment's template (assessment-centric, not legacy model-centric)
  // createRiskFormSelector looks up assessment → template_id → template.taxonomy → risk areas
  // This is the FIX for RTK migration: legacy RiskFormGenerator depends on state.risks.currentModelId
  // which may not be set during autopilot flow, causing empty riskFormData
  const riskFormSelector = useMemo(() => createRiskFormSelector(assessmentUuid), [assessmentUuid]);
  const riskFormTaxonomy = useSelector(riskFormSelector);

  // Step-driven workflow state managed via manageAssessment
  const [currentStep, setCurrentStep] = useState(null); // Will be loaded from backend
  const [assessmentState, setAssessmentState] = useState(null);
  const [isLoadingState, setIsLoadingState] = useState(true);
  const [uploadedBucket, setUploadedBucket] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null); // { step: 'parsing'|'analyzing'|'embedding'|'finalizing', progress: 0-100 }
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentAreaProgress, setCurrentAreaProgress] = useState(null);
  const [approvedNarrative, setApprovedNarrative] = useState(null);
  const [autonomous, setAutonomous] = useState(false);
  const [assessmentMode, setAssessmentMode] = useState('prompt'); // 'prompt' or 'agentic'
  const [isPausing, setIsPausing] = useState(false);
  const [pauseSuccess, setPauseSuccess] = useState(false);
  const [showUploadCard, setShowUploadCard] = useState(true);
  const [templateWarning, setTemplateWarning] = useState(null);

  const steps = [
    { key: 'upload', label: 'Upload Model Card', icon: IconUpload },
    { key: 'context', label: 'Context Discovery', icon: IconBrain },
    { key: 'risks', label: 'Risk Analysis', icon: IconShield },
    { key: 'review', label: 'Review & Refine', icon: IconCheck },
    { key: 'report', label: 'Generate Report', icon: IconFileText }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  // State for pre-uploaded documents (from Model Registration or Inventory)
  const [existingDocuments, setExistingDocuments] = useState([]);

  const loadAssessmentState = useCallback(async () => {
    try {
      setIsLoadingState(true);

      console.log('═══════════════════════════════════════════════════════');
      console.log('[AutopilotFlow] 🚀 LOADING ASSESSMENT STATE');
      console.log('[AutopilotFlow] Model ID:', modelId);
      console.log('═══════════════════════════════════════════════════════');

      // Check for existing documents uploaded during model registration
      let preUploadedDocs = [];
      try {
        const docsResponse = await authFetch(`/.netlify/functions/manageAssessmentDocuments?operation=list&modelUuid=${modelId}&documentType=model_card`);
        if (docsResponse.ok) {
          const docsData = await docsResponse.json();
          preUploadedDocs = docsData.documents || [];
          setExistingDocuments(preUploadedDocs);
          console.log(`[AutopilotFlow] 📄 Found ${preUploadedDocs.length} pre-uploaded document(s) for model`);
        }
      } catch (docErr) {
        console.warn('[AutopilotFlow] ⚠️ Could not check for pre-uploaded documents:', docErr);
      }

      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, operation: 'fetch' })
      });

      if (response.ok) {
        const state = await response.json();
        setAssessmentState(state);
        if (state?.position?.checkpoint) {
          dispatch(saveCheckpoint({ assessmentUuid: state.assessmentUuid, checkpoint: state.position.checkpoint }));
        } else {
          dispatch(clearCheckpoint({ assessmentUuid: state.assessmentUuid }));
        }

        console.log('[AutopilotFlow] 📋 Assessment State Loaded:', {
          assessmentUuid: state.assessmentUuid,
          templateId: state.templateId || 'null (legacy)',
          mode: state.mode || 'unknown',
          autonomous: state.autonomous,
          currentStep: state.currentStep,
          position: state.position
        });

        // Load autonomous flag from backend state
        if (state.autonomous !== undefined && state.autonomous !== null) {
          console.log(`[AutopilotFlow] 🤖 Setting autonomous mode from backend: ${state.autonomous}`);
          setAutonomous(state.autonomous);
        } else {
          console.log('[AutopilotFlow] ⚠️ No autonomous flag in backend state, defaulting to false');
        }

        // Load assessment engine mode from backend state
        if (state.assessmentMode) {
          console.log(`[AutopilotFlow] ⚡ Setting assessment engine from backend: ${state.assessmentMode}`);
          setAssessmentMode(state.assessmentMode);
        }

        // Fetch full template configuration if templateId exists
        let templateConfig = null;
        if (state.templateId) {
          console.log('[AutopilotFlow] 📚 Fetching template configuration for templateId:', state.templateId);

          setTemplateWarning(null);
          try {
            const templateResp = await authFetch('/.netlify/functions/templates-getTemplate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: state.templateId })
            });

            if (templateResp.ok) {
              const template = await templateResp.json();

              templateConfig = {
                use_control_rag: template.use_control_rag || false,
                use_scenario_rag: template.use_scenario_rag || false,
                policy_types: template.policy_types || ['governance', 'security', 'ai-control'],
                policy_documents: template.policy_documents || [],
                risk_catalogues: template.risk_catalogues || []
              };

              console.log('[AutopilotFlow] ✅ Template Configuration Loaded:', {
                templateId: state.templateId,
                templateName: template.name,
                use_control_rag: templateConfig.use_control_rag,
                use_scenario_rag: templateConfig.use_scenario_rag,
                policy_types: templateConfig.policy_types,
                policy_documents_count: templateConfig.policy_documents.length,
                risk_catalogues_count: templateConfig.risk_catalogues.length
              });
            } else {
              console.error('[AutopilotFlow] ❌ Failed to fetch template configuration:', templateResp.status);
            }
          } catch (templateError) {
            console.error('[AutopilotFlow] ❌ Error fetching template:', templateError);
          }
        } else {
          console.log('[AutopilotFlow] ℹ️ No templateId (legacy assessment), skipping template config fetch');
          setTemplateWarning('Template is missing for this assessment; using saved data as fallback.');
        }

        // Set current assessment context with template config (RTK)
        if (state.assessmentUuid) {
          console.log('[AutopilotFlow] 📤 Dispatching RTK setCurrentAssessment with:', {
            modelId,
            assessmentUuid: state.assessmentUuid,
            templateId: state.templateId,
            templateConfig: templateConfig
          });

          // RTK: Set current context in appSlice
          dispatch(setCurrentAssessment(state.assessmentUuid));
          dispatch(setCurrentModel(modelId));

          // RTK: Initialize assessment in assessmentsSlice.byId so risks can be stored/loaded
          // This is critical for the canvas and report pages to work
          dispatch(
            initializeAssessmentLocal({
              assessmentUuid: state.assessmentUuid,
              modelId,
              modelName: state.assessmentName || modelName,
              assessmentName: state.assessmentName,
              // CRITICAL: Don't override status with fallback - preserve existing status from DB
              // Only include assessmentStatus if it has a real value
              ...(state.assessmentStatus && { assessmentStatus: state.assessmentStatus }),
              templateId: state.templateId,
              templateConfig: templateConfig,
              mode: state.mode || 'autopilot',
              autonomous: state.autonomous,
            })
          );

          console.log('[AutopilotFlow] ✅ Assessment initialized in Redux assessmentsSlice.byId');

          console.log('[AutopilotFlow] ✅ Current Assessment Set in Redux:', {
            modelId,
            assessmentUuid: state.assessmentUuid,
            templateId: state.templateId || 'null (legacy)',
            hasTemplateConfig: !!templateConfig,
            configContents: templateConfig
          });
        } else {
          console.warn('[AutopilotFlow] ⚠️ No assessmentUuid found, cannot set current assessment');
        }

        console.log('═══════════════════════════════════════════════════════');

        // Determine current step from state
        const position = state.position || {};
        if (position.step === 'completed' || position.majorStep === 'review') {
          // Assessment completed - fetch risks into Redux before navigating to report
          console.log('[AutopilotFlow] 📥 Fetching saved risks before navigating to report...');
          await dispatch(fetchAssessmentRisks({ assessmentUuid: state.assessmentUuid, modelId }));
          console.log('[AutopilotFlow] ✅ Risks fetched, navigating to report');
          navigate(`/assessment-report/${modelId}`, { state: { assessmentUuid } });
          return;
        } else if (position.majorStep === 'risks' && position.narrativesApproved) {
          // Only enter risks phase if we have valid upload data
          if (position.uploaded?.bucket && position.uploaded?.path) {
            setCurrentStep('risks');
            setUploadedBucket(position.uploaded.bucket);
            setUploadedPath(position.uploaded.path);
          } else {
            console.warn('[AutopilotFlow] ⚠️ Position indicates risks phase but no upload data - resetting to upload');
            setCurrentStep('upload');
          }
        } else if (position.uploaded?.bucket && position.uploaded?.path) {
          setCurrentStep('context');
          setUploadedBucket(position.uploaded.bucket);
          setUploadedPath(position.uploaded.path);
        } else if (preUploadedDocs.length > 0 && !preUploadedDocs[0].processed) {
          // Use pre-uploaded document from Model Registration
          const doc = preUploadedDocs[0];
          console.log('[AutopilotFlow] 📄 Using pre-uploaded document:', doc.file_name);
          // Skip upload, go straight to processing with existing document
          setCurrentStep('upload'); // Stay on upload step but show processing UI
          // The UploadStep will detect existingDocuments and auto-process
        } else {
          setCurrentStep('upload');
        }
      } else {
        // New assessment - check if we have pre-uploaded documents
        if (preUploadedDocs.length > 0) {
          console.log('[AutopilotFlow] 📄 New assessment with pre-uploaded documents');
          setCurrentStep('upload'); // UploadStep will show existing docs
        } else {
          console.log('[AutopilotFlow] 📝 New assessment (no existing state)');
          setCurrentStep('upload');
        }
      }
    } catch (error) {
      console.error('[AutopilotFlow] ❌ Failed to load state:', error);
      setCurrentStep('upload');
    } finally {
      setIsLoadingState(false);
    }
  }, [modelId, assessmentUuid, modelName, navigate, dispatch]);

  // Track if we've already initiated the fetch to prevent loops
  const [riskCardFetched, setRiskCardFetched] = React.useState(false);

  // Initialize Redux model state for autopilot mode
  useEffect(() => {
    // Ensure the model is properly initialized in Redux
    // This is crucial for autopilot mode since it bypasses the normal model loading flow
    // Same initialization as copilot mode in RiskCardForm.js:114
    // Use ref-like state to prevent infinite loops from bridge selector reference changes
    if (modelId && assessmentUuid && !riskCardFetched) {
      console.log('[AutopilotFlow] Initializing Redux model state for:', modelId, assessmentUuid);
      setRiskCardFetched(true);
      dispatch(fetchRiskCardData({ assessmentUuid, modelId }));
    }
  }, [dispatch, modelId, assessmentUuid, riskCardFetched]);

  // Load assessment state on component mount
  useEffect(() => {
    loadAssessmentState();
  }, [loadAssessmentState]);

  /**
   * Handle pause - saves current checkpoint
   */
  const handlePauseAssessment = useCallback(async () => {
    setIsPausing(true);
    setPauseSuccess(false);

    try {
      // The checkpoint is automatically saved by RiskAutopilotV2
      // We just need to confirm to the user that they can safely leave
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for UX

      setPauseSuccess(true);
      console.log('[AutopilotFlow] Assessment paused successfully');

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setPauseSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('[AutopilotFlow] Failed to pause:', error);
    } finally {
      setIsPausing(false);
    }
  }, []);

  /**
   * Handle model card file upload using enhanced progress system
   */
  const handleFileUpload = async (file, result = null) => {
    // If result is provided, it's from our enhanced upload system
    if (result) {
      console.log('[Autopilot] Enhanced upload completed:', result);
      setUploadedBucket(result.bucket);
      setUploadedPath(result.path);
      
      // Store extracted context data - handled by Redux
      if (result.extractedData) {
        console.log('[Autopilot] Extracted context data:', result.extractedData);
      }
      
      setCurrentStep('context');
      return;
    }

    // Legacy fallback for direct file upload (shouldn't be used now)
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    try {
      console.log('[Autopilot] Starting upload...', { fileName: file.name, size: file.size, type: file.type });
      const bucket = 'model-cards';
      const path = `${modelId}/${assessmentUuid}/${file.name}`;

      // Pre-flight: check if any file already exists under this assessment and has embeddings
      const artRes = await authFetch('/.netlify/functions/checkAssessmentArtifacts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid, bucket })
      });
      if (artRes.ok) {
        const info = await artRes.json();
        if (info?.found && info.embeddingsCount > 0) {
          setUploadedBucket(info.bucket);
          setUploadedPath(info.path);
          setCurrentStep('context');
          setIsProcessing(false);
          return;
        }
      }
      const { path: uploadPath } = await uploadViaSupabase({ file, bucket, path, contentType: file.type || 'application/pdf' });
      console.log('[Autopilot] Upload success', { bucket, uploadPath });
      setUploadedBucket(bucket);
      setUploadedPath(uploadPath);
      // Start Llama parsing (returns jobId quickly)
      const startRes = await authFetch('/.netlify/functions/supaLlamaStart', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket, path: uploadPath, fileName: file.name })
      });
      if (!startRes.ok) throw new Error(await startRes.text().catch(()=> 'Start failed'));
      const { jobId } = await startRes.json();
      console.log('[Autopilot] Llama parse started', { jobId });

      // Poll finalize until succeeded (show minimal progress)
      let attempts = 0;
      while (attempts < 30) {
        const finRes = await authFetch('/.netlify/functions/supaLlamaFinalize', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId, bucket, path: uploadPath, fileName: file.name, modelId, assessmentUuid, saveMarkdown: true })
        });
        if (finRes.status === 202) {
          const body = await finRes.json().catch(()=>({}));
          console.log('[Autopilot] Parse not ready', { status: body.status, attempts });
          // show some progress during long parsing
          if (attempts >= 1 && currentStep === 'upload') {
            // progress hint (upload+parse in progress)
          }
          attempts += 1;
          const delayMs = Math.min(5000, 1000 * Math.pow(1.5, attempts));
          console.log('[Autopilot] Waiting for parse…', { attempts, delayMs });
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }
        if (!finRes.ok) {
          const txt = await finRes.text().catch(()=> 'Finalize failed');
          throw new Error(txt);
        }
        const finalBody = await finRes.json().catch(()=>({}));
        console.log('[Autopilot] Finalize done', finalBody);
        break;
      }
      console.log('[Autopilot] LlamaParse finalized; starting narrative generation UI');
      // Persist upload completion to assessment state
      const uploadPosition = {
        majorStep: 'context',
        uploaded: { bucket, path: uploadPath },
        stepCompleted: 'upload'
      };
      await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'update',
          position: uploadPosition
        })
      });
      // Sync position to RTK state for progress tracking
      dispatch(updateAssessmentPositionLocal({ assessmentUuid, position: uploadPosition, currentStep: 'context' }));

      // Update UI state
      setUploadedBucket(bucket);
      setUploadedPath(uploadPath);
      setCurrentStep('context');
    } catch (err) {
      setError(`Failed to process model card: ${err.message}`);
      console.error('[Autopilot] Error during upload/process:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // removed extraction-based gap flow in favor of narrative-first

 
  /**
   * Enhanced model card deletion with proper UX
   */
  const handleDeleteModelCard = async () => {
    setIsDeleting(true);
    setError(null);
    
    try {
      // Try to find any existing artifact and delete it
      const res = await authFetch('/.netlify/functions/checkAssessmentArtifacts', {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid, bucket: 'model-cards' })
      });
      
      if (res.ok) {
        const info = await res.json();
        if (info?.found) {
          const del = await authFetch('/.netlify/functions/deleteModelCardSupabase', {
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bucket: info.bucket, path: info.path })
          });
          
          if (!del.ok) {
            throw new Error(await del.text().catch(() => 'Delete failed'));
          }
          
          // Reset state after successful deletion
          setUploadedBucket(null);
          setUploadedPath(null);
          setCurrentStep('upload');
        }
      }
      
      setDeleteModalOpen(false);
    } catch (e) {
      setError(`Failed to delete: ${e.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // (removed client-side text reader; backend handles extraction)

  // Show AI robot loading screen while authenticating
  if (authLoading || !isAuthenticated) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="xl">
          <AIGeneratingIcon isGenerating size={64} color="#228be6" />
          <Text size="lg" c="dimmed" fw={500}>
            Loading assessment...
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Clean Header - Model Focus */}
        <Card withBorder p="md">
          <Group justify="space-between" align="center">
            <div>
              <Text size="lg" fw={700} c="blue">
                {modelName || modelId} Assessment
              </Text>
            </div>
            
            {/* Delete button moved to far right */}
            {uploadedBucket && uploadedPath && (
              <Tooltip label="Delete assessment" withArrow>
                <ActionIcon 
                  variant="subtle" 
                  color="red" 
                  size="md"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Card>


        {/* Enhanced Error Alert */}
        {error && (
          <Alert 
            icon={<IconAlertCircle size="1rem" />} 
            title="Something went wrong" 
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
            style={{ maxWidth: '600px', margin: '0 auto' }}
          >
            <Text size="sm">
              {error}
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              You can try uploading again or switch to Co-pilot mode for manual control.
            </Text>
          </Alert>
        )}

        {/* Template warning */}
        {templateWarning && (
          <Alert 
            icon={<IconAlertCircle size="1rem" />} 
            title="Template missing"
            color="yellow"
            variant="light"
            withCloseButton
            onClose={() => setTemplateWarning(null)}
            style={{ maxWidth: '600px', margin: '0 auto' }}
          >
            <Text size="sm">
              {templateWarning} Risk areas and impact categories will use saved data as a fallback.
            </Text>
          </Alert>
        )}

        {/* Loading State */}
        {isLoadingState && (
          <Card withBorder radius="md" p="xl">
            <Group justify="center" gap="md">
              <IconLoader size={24} className="rotate-animation" />
              <Text>Loading assessment state...</Text>
            </Group>
          </Card>
        )}

        {/* Step Content */}
        {!isLoadingState && (
          <Transition mounted transition="fade" duration={300}>
            {(styles) => (
              <div style={styles}>
                {currentStep === 'upload' && showUploadCard && (
                  <UploadStep
                    onFileUpload={(...args) => {
                      setShowUploadCard(false);
                      handleFileUpload(...args);
                    }}
                    isProcessing={isProcessing}
                    processingStatus={processingStatus}
                    existingData={Object.keys(existingContextData).length > 0}
                    assessmentState={assessmentState}
                    modelId={modelId}
                    assessmentUuid={assessmentUuid}
                    existingDocuments={existingDocuments}
                    onProcessExistingDoc={async (doc) => {
                      // Process an existing document from assessment_documents table
                      console.log('[Autopilot] Processing existing document:', doc.file_name);
                      // Keep showUploadCard=true so UploadStep stays mounted and shows progress UI
                      setIsProcessing(true);
                      setProcessingStatus({ step: 'parsing', progress: 10, fileName: doc.file_name });
                      setError(null);

                      try {
                        // Start processing the existing document
                        const bucket = doc.bucket || 'model-cards';
                        const path = doc.file_path;

                        // Start Llama parsing
                        setProcessingStatus({ step: 'parsing', progress: 20, fileName: doc.file_name });
                        const startRes = await authFetch('/.netlify/functions/supaLlamaStart', {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ bucket, path, fileName: doc.file_name })
                        });
                        if (!startRes.ok) throw new Error(await startRes.text().catch(()=> 'Start failed'));
                        const { jobId } = await startRes.json();
                        console.log('[Autopilot] Llama parse started for existing doc', { jobId });

                        // Poll finalize until succeeded
                        setProcessingStatus({ step: 'analyzing', progress: 35, fileName: doc.file_name });
                        let attempts = 0;
                        while (attempts < 30) {
                          const finRes = await authFetch('/.netlify/functions/supaLlamaFinalize', {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ jobId, bucket, path, fileName: doc.file_name, modelId, assessmentUuid, saveMarkdown: true })
                          });
                          if (finRes.status === 202) {
                            attempts += 1;
                            // Update progress during polling (35% to 70%)
                            const pollProgress = Math.min(70, 35 + (attempts * 2));
                            setProcessingStatus({ step: 'analyzing', progress: pollProgress, fileName: doc.file_name });
                            const delayMs = Math.min(5000, 1000 * Math.pow(1.5, attempts));
                            await new Promise((resolve) => setTimeout(resolve, delayMs));
                            continue;
                          }
                          if (!finRes.ok) throw new Error(await finRes.text().catch(()=> 'Finalize failed'));
                          console.log('[Autopilot] Finalize done for existing doc');
                          break;
                        }

                        // Mark document as processed
                        setProcessingStatus({ step: 'embedding', progress: 80, fileName: doc.file_name });
                        await authFetch('/.netlify/functions/manageAssessmentDocuments', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            operation: 'markProcessed',
                            documentId: doc.id
                          })
                        });

                        // Update assessment position
                        setProcessingStatus({ step: 'finalizing', progress: 95, fileName: doc.file_name });
                        const uploadPosition = {
                          majorStep: 'context',
                          uploaded: { bucket, path },
                          stepCompleted: 'upload'
                        };
                        await authFetch('/.netlify/functions/manageAssessment', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            modelId,
                            assessmentUuid,
                            operation: 'update',
                            position: uploadPosition
                          })
                        });
                        dispatch(updateAssessmentPositionLocal({ assessmentUuid, position: uploadPosition, currentStep: 'context' }));

                        setProcessingStatus({ step: 'done', progress: 100, fileName: doc.file_name });
                        setUploadedBucket(bucket);
                        setUploadedPath(path);
                        setCurrentStep('context');
                      } catch (err) {
                        setError(`Failed to process document: ${err.message}`);
                        console.error('[Autopilot] Error processing existing doc:', err);
                        // showUploadCard stays true, so user sees error on the UploadStep
                      } finally {
                        setIsProcessing(false);
                        setProcessingStatus(null);
                      }
                    }}
                    onResumeAssessment={(resumeStep) => {
                      console.log('[Autopilot] Resuming assessment from step:', resumeStep, 'with position:', assessmentState?.position);
                      // Restore upload info
                      if (assessmentState?.position?.uploaded?.bucket && assessmentState?.position?.uploaded?.path) {
                        setUploadedBucket(assessmentState.position.uploaded.bucket);
                        setUploadedPath(assessmentState.position.uploaded.path);
                      } else {
                        console.warn('[Autopilot] ⚠️ No upload data in position when resuming');
                      }
                      setCurrentStep(resumeStep);
                    }}
                    onUseExisting={() => {
                      // Route to manual co-pilot form if desired
                      navigate(`/risk-form/${modelId}/unknown/${assessmentUuid}`);
                    }}
                  />
                )}
              
              {currentStep === 'context' && (
                <RiskNarrativeCard
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  bucket={uploadedBucket}
                  path={uploadedPath}
                  riskFormData={riskFormTaxonomy}
                  onApproved={async (narrative) => {
                    console.log('[Autopilot] All narratives approved; starting risk automation');
                    setApprovedNarrative(narrative); // Store the approved narrative
                    const risksPosition = {
                      majorStep: 'risks',
                      uploaded: { bucket: uploadedBucket, path: uploadedPath },
                      narrativesApproved: true,
                      stepCompleted: 'context'
                    };
                    try {
                      // Persist narrative approval to assessment state
                      await authFetch('/.netlify/functions/manageAssessment', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          modelId,
                          assessmentUuid,
                          operation: 'update',
                          position: risksPosition
                        })
                      });
                      // Sync position to RTK state for progress tracking
                      dispatch(updateAssessmentPositionLocal({ assessmentUuid, position: risksPosition, currentStep: 'risks' }));
                    } catch (e) {
                      console.error('[Autopilot] Failed to persist narrative approval:', e);
                    }
                    setCurrentStep('risks');
                  }}
                />
              )}
              
              {currentStep === 'risks' && (
                <Stack gap="md">
                  {/* Resume Banner */}
                  {assessmentState?.position?.pausedAt && (
                    <Alert
                      icon={<IconPlayerPlay size={20} />}
                      title="Resuming Assessment"
                      color="blue"
                      variant="light"
                    >
                      <Text size="sm">
                        Continuing from <strong>{assessmentState.position.currentArea || 'last position'}</strong>
                        {assessmentState.position.completedAreas?.length > 0 && (
                          <>. {assessmentState.position.completedAreas.length} area(s) already completed</>
                        )}.
                      </Text>
                    </Alert>
                  )}

                  {/* Assessment Tracker Card with Ring, Badges, and Mode Toggles */}
                  <Card withBorder p="md" mb="lg">
                    <Stack gap="md">
                      {/* Header with Pause Button */}
                      <Group justify="space-between" align="center">
                        <Text size="md" fw={600}>Assessment Tracker</Text>
                        <Tooltip
                          label={pauseSuccess ? "Progress saved! You can leave and resume later" : "Pause & save progress"}
                          position="left"
                          withArrow
                        >
                          <ActionIcon
                            onClick={handlePauseAssessment}
                            loading={isPausing}
                            color={pauseSuccess ? "green" : "blue"}
                            variant={pauseSuccess ? "light" : "subtle"}
                            size="lg"
                            style={{ transition: 'all 0.2s ease' }}
                          >
                            {pauseSuccess ? <IconCheck size={20} /> : <IconPlayerPause size={20} />}
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                      
                      {/* Two-Section Layout with Proper 2/3 - 1/3 Split */}
                      <Group align="flex-start" wrap="nowrap" style={{ width: '100%' }}>
                        {/* Left Section: Ring Progress and Badges - 2/3 space */}
                        <Stack gap="md" style={{ flex: 2, minWidth: 0 }}>
                          {/* Ring Progress and Current Area */}
                          <Group gap="md" align="center">
                            <RingProgress
                              size={60}
                              thickness={6}
                              sections={[
                                { 
                                  value: currentAreaProgress ? (currentAreaProgress.currentIndex / currentAreaProgress.total) * 100 : 0, 
                                  color: 'blue' 
                                }
                              ]}
                              label={
                                <Text size="xs" fw={700} ta="center">
                                  {currentAreaProgress ? `${currentAreaProgress.currentIndex}/${currentAreaProgress.total}` : '0/7'}
                                </Text>
                              }
                            />
                            <div>
                              <Text size="sm" fw={500}>
                                {currentAreaProgress 
                                  ? `Current: ${currentAreaProgress.currentArea}` 
                                  : 'Ready to begin risk analysis'
                                }
                              </Text>
                            </div>
                          </Group>
                          
                          {/* Assessment Phase Badges */}
                          <Group gap="xs" wrap="wrap">
                            {steps.map((step, index) => {
                              const isActive = index === currentStepIndex;
                              const isCompleted = index < currentStepIndex;
                              const StepIcon = step.icon;
                              
                              return (
                                <Badge 
                                  key={step.key}
                                  size="sm"
                                  variant={isActive ? "filled" : isCompleted ? "light" : "outline"}
                                  color={isCompleted ? 'green' : isActive ? 'blue' : 'gray'}
                                  leftSection={<StepIcon size={12} />}
                                >
                                  {step.label.replace(' Model Card', '').replace(' Discovery', '').replace(' & Refine', '')}
                                </Badge>
                              );
                            })}
                          </Group>
                        </Stack>
                        
                        {/* Gentle Divider */}
                        <Divider orientation="vertical" size="xs" color="gray.3" style={{ margin: '0 12px' }} />
                        
                        {/* Right Section: Assessment Mode Controls - 1/3 space */}
                        <Stack gap="md" style={{ flex: 1, minWidth: 120 }} align="center">
                          <Text size="sm" c="dimmed" fw={500}>Assessment Mode</Text>
                          
                          <Stack gap="sm" align="center" style={{ width: '100%' }}>
                            {/* Supervised/Autonomous Toggle */}
                            <Group gap="xs" align="center" style={{ 
                              padding: '4px 8px', 
                              borderRadius: '6px', 
                              backgroundColor: 'var(--mantine-color-gray-0)',
                              width: '100%',
                              justifyContent: 'space-between'
                            }}>
                              <Text size="xs" c={!autonomous ? "blue" : "dimmed"} fw={!autonomous ? 600 : 400}>
                                Supervised
                              </Text>
                              <Switch
                                checked={autonomous}
                                onChange={(event) => {
                                  const newValue = event.currentTarget.checked;
                                  console.log(`[AutopilotFlow] 🤖 AUTONOMOUS SWITCH TOGGLED: ${autonomous} -> ${newValue}`);
                                  setAutonomous(newValue);
                                }}
                                color="green"
                                size="sm"
                                styles={{
                                  track: { backgroundColor: autonomous ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-4)' }
                                }}
                              />
                              <Text size="xs" c={autonomous ? "green" : "dimmed"} fw={autonomous ? 600 : 400}>
                                Autonomous
                              </Text>
                            </Group>

                          </Stack>
                        </Stack>
                      </Group>
                    </Stack>
                  </Card>

                  <RiskAutopilotV2
                    modelId={modelId}
                    assessmentUuid={assessmentUuid}
                    bucket={uploadedBucket}
                    path={uploadedPath}
                    approvedNarrative={approvedNarrative}
                    autonomous={autonomous}
                    assessmentMode={assessmentMode}
                    // resumeFromPosition now read from Redux via selectResumeData
                    onAreaProgress={setCurrentAreaProgress}
                    onComplete={async () => {
                    console.log('[Autopilot] Complete risk analysis (all phases); transitioning to review');
                    setCurrentAreaProgress(null); // Clear area progress
                    const reviewPosition = {
                      majorStep: 'review',
                      stepCompleted: 'risks',
                      uploaded: { bucket: uploadedBucket, path: uploadedPath },
                      narrativesApproved: true
                    };
                    try {
                      // Persist risk completion
                      await authFetch('/.netlify/functions/manageAssessment', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          modelId,
                          assessmentUuid,
                          operation: 'update',
                          position: reviewPosition
                        })
                      });
                      // Sync position to RTK state for progress tracking
                      dispatch(updateAssessmentPositionLocal({
                        assessmentUuid,
                        position: reviewPosition,
                        currentStep: 'review',
                        assessmentStatus: 'Completed' // Mark as completed when reaching summary
                      }));

                      // Update assessment status to Completed when reaching summary step
                      // (User requested: assessment should complete on summary, not on "sees report")
                      console.log('[Autopilot] Marking assessment as Completed on summary step...');
                      await dispatch(updateAssessmentStatusByUuid({ modelId, assessmentUuid, status: 'Completed' })).unwrap();
                      console.log('[Autopilot] Assessment status updated to Completed');
                      // Clear any persisted checkpoint now that review is reached
                      await dispatch(setAssessmentCheckpoint({ modelId, assessmentUuid, checkpoint: null })).unwrap();
                      dispatch(clearCheckpoint({ assessmentUuid }));
                    } catch (e) {
                      console.error('[Autopilot] Failed to persist risk completion:', e);
                      setError('Could not persist completion. Please try again.');
                      return;
                    }
                    setCurrentStep('review');
                  }}
                  />
                </Stack>
              )}
              
              {currentStep === 'review' && (
                <ReviewStep
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  onGenerateReport={() => setCurrentStep('report')}
                />
              )}
              
              {currentStep === 'report' && (
                <ReportStep
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  assessmentName={assessmentState?.assessmentName || modelName}
                />
              )}
              </div>
            )}
          </Transition>
        )}

        {/* Elegant Deletion Confirmation Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title={
            <Group gap="sm">
              <IconTrash size={20} color="#e03131" />
              <Text fw={600}>Delete Model Card</Text>
            </Group>
          }
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              This will permanently delete your uploaded model card and all associated embeddings. 
              You'll need to re-upload to continue your assessment.
            </Text>
            
            <Group justify="flex-end" gap="sm">
              <Button 
                variant="subtle" 
                onClick={() => setDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button 
                color="red" 
                loading={isDeleting}
                onClick={handleDeleteModelCard}
                leftSection={<IconTrash size={16} />}
              >
                Delete Model Card
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
}

/**
 * Upload Step Component
 */
function UploadStep({
  onFileUpload,
  isProcessing,
  processingStatus,
  existingData,
  assessmentState,
  onResumeAssessment,
  onUseExisting,
  modelId,
  assessmentUuid,
  existingDocuments = [],
  onProcessExistingDoc,
  continueLabel = 'Continue with existing assessment data'
}) {
  // Check for unprocessed documents uploaded during model registration
  const unprocessedDocs = existingDocuments.filter(doc => !doc.processed);
  const hasPreUploadedDocs = unprocessedDocs.length > 0;

  // Processing step labels for the UI
  const processingSteps = [
    { key: 'parsing', label: 'Parsing' },
    { key: 'analyzing', label: 'Analyzing' },
    { key: 'embedding', label: 'Embedding' },
    { key: 'finalizing', label: 'Finalizing' }
  ];

  // Render processing feedback UI (Option B design)
  if (isProcessing && processingStatus) {
    return (
      <Card withBorder radius="md" p="xl">
        <Stack gap="lg" align="center">
          {/* Pulsating robot icon */}
          <AIGeneratingIcon isGenerating={true} size={64} />

          <Stack gap="xs" align="center">
            <Text size="xl" fw={600}>Processing Documentation</Text>
            <Text size="sm" c="dimmed">{processingStatus.fileName}</Text>
          </Stack>

          {/* Progress bar */}
          <Stack gap="xs" style={{ width: '100%', maxWidth: 400 }}>
            <Progress
              value={processingStatus.progress}
              size="lg"
              radius="xl"
              color="blue"
              animated
            />
            <Text size="xs" c="dimmed" ta="center">{processingStatus.progress}%</Text>
          </Stack>

          {/* Step indicators */}
          <Group gap="md" justify="center">
            {processingSteps.map((step, idx) => {
              const currentIdx = processingSteps.findIndex(s => s.key === processingStatus.step);
              const isDone = idx < currentIdx || processingStatus.step === 'done';
              const isCurrent = step.key === processingStatus.step;

              return (
                <Group key={step.key} gap={4}>
                  {isDone ? (
                    <IconCheck size={14} color="#40c057" />
                  ) : isCurrent ? (
                    <IconLoader size={14} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Text size="xs" c="dimmed">○</Text>
                  )}
                  <Text size="xs" c={isDone ? 'green' : isCurrent ? 'blue' : 'dimmed'} fw={isCurrent ? 600 : 400}>
                    {step.label}
                  </Text>
                </Group>
              );
            })}
          </Group>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="xl">
      <Stack gap="lg" align="center">
        {/* Show pre-uploaded documents section if they exist */}
        {hasPreUploadedDocs && (
          <>
            <Stack gap="sm" align="center">
              <Group gap="md" align="center">
                <IconFileDescription size={64} color="#40c057" />
                <Stack gap="xs" align="flex-start">
                  <Text size="xl" fw={600}>Model Documentation Ready</Text>
                  <Text size="sm" c="dimmed" maw={400}>
                    You uploaded documentation during model registration.
                    Click "Process Documentation" to analyze it and generate your risk assessment.
                  </Text>
                </Stack>
              </Group>
            </Stack>

            {/* Show uploaded documents */}
            <Card withBorder p="md" bg="green.0" style={{ maxWidth: '500px', width: '100%' }}>
              <Stack gap="sm">
                <Group gap="sm">
                  <IconCheck size={20} color="#40c057" />
                  <Text fw={600} c="green.8">Uploaded Documents</Text>
                </Group>
                {unprocessedDocs.map(doc => (
                  <Group key={doc.id} gap="xs" wrap="nowrap">
                    <IconFileText size={16} color="#666" />
                    <Text size="sm" truncate style={{ flex: 1 }}>{doc.file_name}</Text>
                    <Badge size="xs" variant="light" color="gray">
                      {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}
                    </Badge>
                  </Group>
                ))}
                <Button
                  variant="filled"
                  color="green"
                  mt="sm"
                  loading={isProcessing}
                  onClick={() => onProcessExistingDoc?.(unprocessedDocs[0])}
                  leftSection={<IconBrain size={16} />}
                >
                  {isProcessing ? 'Processing...' : 'Process Documentation'}
                </Button>
              </Stack>
            </Card>

            <Divider label="or upload different documentation" labelPosition="center" w="100%" />
          </>
        )}

        {/* Standard upload section */}
        {!hasPreUploadedDocs && (
          <Stack gap="sm" align="center">
            <Group gap="md" align="center">
              <IconUpload size={64} color="#1976d2" />
              <Stack gap="xs" align="flex-start">
                <Text size="xl" fw={600}>Upload Your Model Documentation</Text>
                <Text size="sm" c="dimmed" maw={400}>
                  Drop your model card, documentation, or system specs. Huginn will analyze it
                  and automatically generate a comprehensive risk assessment.
                </Text>
              </Stack>
            </Group>
          </Stack>
        )}

        <Stack gap="md" align="center">
          <ModelCardUploadButton
            modelId={modelId}
            assessmentUuid={assessmentUuid}
            onSuccess={(result) => {
              onFileUpload(null, result); // Pass result to existing handler
            }}
            onError={(error) => {
              console.error('[Autopilot] Upload error:', error);
            }}
            buttonText={hasPreUploadedDocs ? "Upload Different Document" : "Choose Model Card"}
            size="lg"
            variant={hasPreUploadedDocs ? "light" : "filled"}
          />
        </Stack>

        <Text size="sm" c="dimmed">
          Supported: PDF, JSON, Markdown, Text, Word documents
        </Text>

        {/* Resume Assessment for Returning Users */}
        {assessmentState?.position?.uploaded && (
          <>
            <Divider label="Continue Assessment" labelPosition="center" w="100%" />
            <Card withBorder p="md" bg="blue.0" style={{ maxWidth: '500px' }}>
              <Stack gap="sm">
                <Group gap="sm">
                  <IconPlayerPlay size={20} color="#1976d2" />
                  <Text fw={600} c="blue">
                    Assessment in Progress
                  </Text>
                </Group>
                <Text size="sm" c="dimmed">
                  You have an ongoing assessment. Resume where you left off or start fresh.
                </Text>
                <Group gap="sm" justify="center">
                  {assessmentState.position.narrativesApproved ? (
                    <Button
                      variant="filled"
                      onClick={() => onResumeAssessment('risks')}
                      leftSection={<IconPlayerPlay size={16} />}
                    >
                      Resume Risk Analysis
                    </Button>
                  ) : (
                    <Button
                      variant="filled"
                      onClick={() => onResumeAssessment('context')}
                      leftSection={<IconPlayerPlay size={16} />}
                    >
                      Resume Narrative Review
                    </Button>
                  )}
                  <Button variant="subtle" size="sm">
                    Start Fresh
                  </Button>
                </Group>
              </Stack>
            </Card>
          </>
        )}

        {existingData && !assessmentState?.position?.uploaded && !hasPreUploadedDocs && (
          <>
            <Divider label="or" labelPosition="center" w="100%" />
            <Button variant="light" onClick={onUseExisting}>
              {continueLabel}
            </Button>
          </>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Assessment Overview/Collage Dashboard
 */
function ReviewStep({ modelId, assessmentUuid, onGenerateReport }) {
  const dispatch = useDispatch();

  // Redux state via bridge selectors (reads new slice first, falls back to legacy)
  const risksState = useSelector((s) => s.riskItems.byAssessment?.[assessmentUuid] || {});
  const riskFormData = risksState.modelRisks || risksState.riskFormData || {};
  const savedControls = useSelector(selectSavedControlsBridge(assessmentUuid, modelId));
  // Direct RTK slice access (no bridge) - assessment-centric residual risk state
  const residualRiskState = useSelector(
    (state) => state.residualRiskRTK?.byAssessment?.[assessmentUuid] || {}
  );
  const securityState = useSelector(selectSecurityBridge(assessmentUuid, modelId));

  // Fetch residual risk data on mount (required for showing assessed risks count)
  useEffect(() => {
    if (assessmentUuid && modelId) {
      console.log('[ReviewStep] Fetching residual risk data...');
      dispatch(fetchResidualRisks({ assessmentUuid }));
    }
  }, [dispatch, assessmentUuid, modelId]);

  const areas = Object.keys(riskFormData);

  // Check if security assessment is complete (has scenarios)
  const securityComplete = securityState.scenarios && Object.keys(securityState.scenarios).length > 0;

  // Area status calculation - removed getAreaStats as area cards are no longer displayed
  const completedAreas = areas.filter(areaName => {
    const areaRisks = riskFormData[areaName]?.areaRisks || riskFormData[areaName]?.sectionRisks || {};
    const hasRisks = Object.values(areaRisks).some(risks =>
      Array.isArray(risks) && risks.some(risk => risk.status === 'Saved')
    );

    if (hasRisks) {
      const hasEvaluations = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => risk.priority)
      );
      const hasActions = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => savedControls[risk.riskId]?.length > 0)
      );
      return hasEvaluations && hasActions;
    }
    return false;
  }).length;

  const partialAreas = areas.filter(areaName => {
    const areaRisks = riskFormData[areaName]?.areaRisks || riskFormData[areaName]?.sectionRisks || {};
    const hasRisks = Object.values(areaRisks).some(risks =>
      Array.isArray(risks) && risks.some(risk => risk.status === 'Saved')
    );

    if (hasRisks) {
      const hasEvaluations = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => risk.priority)
      );
      const hasActions = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => savedControls[risk.riskId]?.length > 0)
      );
      return !(hasEvaluations && hasActions);
    }
    return false;
  }).length;

  // Add security to completed count if it has scenarios
  const totalCompletedAreas = completedAreas + (securityComplete ? 1 : 0);
  const totalAreas = areas.length + 1; // 6 risk areas + 1 security area

  return (
    <Stack gap="lg">

      {/* Assessment Summary & Report */}
      <ResidualRiskSummary
        modelId={modelId}
        assessmentUuid={assessmentUuid}
        riskFormData={riskFormData}
        savedControls={savedControls}
        residualRiskState={residualRiskState}
        completedAreas={totalCompletedAreas}
        partialAreas={partialAreas}
        totalAreas={totalAreas}
        onGenerateReport={onGenerateReport}
      />

{/* Old Assessment Summary section removed - functionality moved to ResidualRiskSummary component above */}
    </Stack>
  );
}

/**
 * Report Step Component - Navigate to Assessment Report
 * NOTE: Assessment status is already marked as 'Completed' when reaching the review/summary step.
 * This component only handles navigation to the report.
 */
function ReportStep({ modelId, assessmentUuid, assessmentName }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false); // Guard to prevent duplicate navigation

  // Navigate to report (assessment is already complete from summary step)
  useEffect(() => {
    if (!modelId || !assessmentUuid) return;
    if (hasNavigatedRef.current) return; // Prevent re-execution on remount
    hasNavigatedRef.current = true; // Set flag before async operations

    const navigateToReport = async () => {
      try {
        const completedPosition = {
          step: 'completed',
          majorStep: 'review',
          completedAt: new Date().toISOString(),
          source: 'autopilot'
        };

        // Update position to 'completed' (status already set to 'Completed' at summary step)
        dispatch(updateAssessmentPositionLocal({
          assessmentUuid,
          position: completedPosition,
          currentStep: 'completed',
          assessmentName
        }));

        // Update position in DB
        await authFetch('/.netlify/functions/manageAssessment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            modelId,
            assessmentUuid,
            operation: 'updatePosition',
            position: completedPosition
          })
        });

        // Fetch risks into Redux before navigating to report
        console.log('[ReportStep] Fetching saved risks before navigating to report...');
        await dispatch(fetchAssessmentRisks({ assessmentUuid, modelId }));

        console.log('[ReportStep] Navigating to report (status already Completed from summary)');

        // Navigate to editable report (pass assessmentUuid and assessmentName via router state)
        navigate(`/assessment-report/${modelId}`, { state: { assessmentUuid, assessmentName } });
      } catch (error) {
        console.error('[ReportStep] Failed to navigate to report:', error);
      }
    };

    navigateToReport();
  }, [modelId, assessmentUuid, assessmentName, dispatch, navigate]);

  return (
    <Card withBorder radius="md" p="xl">
      <Stack align="center" gap="md">
        <IconCheck size={48} color="green" />
        <Text size="lg" fw={600}>Assessment Completed!</Text>
        <Text c="dimmed">Redirecting to your report...</Text>
      </Stack>
    </Card>
  );
}

/**
 * Assessment Summary & Report Component
 */
function ResidualRiskSummary({ modelId, assessmentUuid, riskFormData, savedControls, residualRiskState, completedAreas, partialAreas, totalAreas, onGenerateReport }) {
  // Count risks with residual for display
  const risksWithResidualCount = Object.entries(riskFormData).reduce((count, [areaName, areaData]) => {
    const areaRisks = areaData?.areaRisks || areaData?.sectionRisks || {};
    return count + Object.values(areaRisks).reduce((areaCount, risks) => {
      if (Array.isArray(risks)) {
        return areaCount + risks.filter(risk => risk.status === 'Saved' && residualRiskState?.[risk.riskId]).length;
      }
      return areaCount;
    }, 0);
  }, 0);

  // Check if assessment is complete
  const isComplete = completedAreas === totalAreas && totalAreas > 0;

  // Navigate to report (user action) - triggers ReportStep which updates status
  const handleSeeReport = () => {
    if (onGenerateReport) {
      onGenerateReport();
    }
  };

  return (
    <Card withBorder p="lg">
      <Stack gap="md">
        {/* Completion Banner */}
        {isComplete && (
          <Alert
            icon={<IconCheck size={20} />}
            title="Assessment Complete!"
            color="green"
            variant="light"
          >
            <Text size="sm">
              All areas have been assessed. Click "See Report" to view the full assessment report.
            </Text>
          </Alert>
        )}

        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" align="center" style={{ flex: 2 }}>
            <Text size="lg" fw={600}>
              Assessment Summary
            </Text>
            <Group gap="xs" wrap="wrap">
              <Badge color="green" size="sm">
                {completedAreas} Completed
              </Badge>
              <Badge color="orange" size="sm">
                {partialAreas} Partial
              </Badge>
              <Badge color="gray" size="sm">
                {totalAreas - completedAreas - partialAreas} Pending
              </Badge>
              <Badge size="sm" variant="light" color="blue">
                {risksWithResidualCount} Risks Assessed
              </Badge>
            </Group>
          </Group>
          {/* See Report button */}
          <Button
            variant="filled"
            color="blue"
            onClick={handleSeeReport}
            leftSection={<IconFileDescription size={16} />}
          >
            See Report
          </Button>
        </Group>

        <Text size="sm" c="dimmed">
          Post-mitigation risk levels after implementing recommended controls and actions.
        </Text>

        <ResidualRiskCards
          riskFormData={riskFormData}
          residualRiskState={residualRiskState}
          savedControls={savedControls}
        />
      </Stack>
    </Card>
  );
}